import React, { useEffect, useState } from "react";

import { TFunction, formatCO2Amount, formatCurrency, useTranslation } from "utils";

import {
  CROP_EXPLORER_RESULT_CALCULATION_TYPE,
  CROP_EXPLORER_RESULT_TOGGLE_OPTION,
  CROP_EXPLORER_RESULT_TYPE,
  Calculation,
  CropExplorationResponse,
  YearRange,
} from "models";

import { Divider, ExtraInfo, Flex, Icon, KlimRadioGroup, LinkMoreInfo } from "components";

import { useCropExplorationContext } from "pages/app/crop-explorer/CropExplorationContext";
import { CropExplorerBarChart } from "pages/app/crop-explorer/components/CropExplorerBarChart";

type CropExplorerResultCardPropsEmpty = {
  iconName: string;
  title: string;
  description: string;
};

type CropExplorerResultCardProps = {
  resultType: CROP_EXPLORER_RESULT_TYPE;
  calculationType: CROP_EXPLORER_RESULT_CALCULATION_TYPE;
  showResults: boolean;
  yearRange?: YearRange;
  calculation?: Calculation;
};

export const CropExplorerResultCard = ({ resultType, calculationType, calculation, showResults, yearRange }: CropExplorerResultCardProps) => {
  const { t } = useTranslation("cropExplorer");
  const { cropExploration } = useCropExplorationContext();

  const [hasNegativeImpact, setHasNegativeImpact] = useState<boolean>();
  const [showNegativePayoutDrawer, setShowNegativePayoutDrawer] = useState<boolean>(false);
  const [avg, setAvg] = useState<string>();
  const [min, setMin] = useState<string>();
  const [max, setMax] = useState<string>();
  const [sequesteredAvg, setSequesteredAvg] = useState<string>();

  const [emptyStateValue, setEmptyStateValue] = useState<CropExplorerResultCardPropsEmpty>();
  const [toggleOption, setToggleOption] = useState<CROP_EXPLORER_RESULT_TOGGLE_OPTION>(CROP_EXPLORER_RESULT_TOGGLE_OPTION.YEAR);

  const hasBaseline = typeof calculation?.emissions.baseline === "number";

  const setValues = () => {
    if (!calculation) return;
    if (resultType === CROP_EXPLORER_RESULT_TYPE.PAYOUT && calculation.payout) {
      setHasNegativeImpact(calculation.payout.total.avg < 0);

      if (calculationType === CROP_EXPLORER_RESULT_CALCULATION_TYPE.TOTAL) {
        setAvg(formatPayoutValue(calculation.payout.total.avg));
        setMin(formatPayoutValue(calculation.payout.total.min));
        setMax(formatPayoutValue(calculation.payout.total.max));
      } else {
        setAvg(formatPayoutValue(dividesPerHectares(calculation.payout.total.avg), true));
        setMin(formatPayoutValue(dividesPerHectares(calculation.payout.total.min), true));
        setMax(formatPayoutValue(dividesPerHectares(calculation.payout.total.max), true));
      }
    }
    if (resultType === CROP_EXPLORER_RESULT_TYPE.EMISSION) {
      const hasNegativeImpact = !calculation.payout
        ? !!calculation.emissions.baseline && calculation.emissions.baseline > 0
        : calculation.emissions.difference.avg > 0;
      setHasNegativeImpact(hasNegativeImpact);

      if (calculationType === CROP_EXPLORER_RESULT_CALCULATION_TYPE.TOTAL) {
        setAvg(formatEmissionValue(calculation.emissions.difference.avg));
        setMin(formatEmissionValue(calculation.emissions.difference.min));
        setMax(formatEmissionValue(calculation.emissions.difference.max));
        if (hasBaseline) setSequesteredAvg(formatEmissionValue(calculation.emissions.baseline as number));
      } else {
        setAvg(formatEmissionValue(dividesPerHectares(calculation.emissions.difference.avg), true));
        setMin(formatEmissionValue(dividesPerHectares(calculation.emissions.difference.min), true));
        setMax(formatEmissionValue(dividesPerHectares(calculation.emissions.difference.max), true));
        if (hasBaseline) setSequesteredAvg(formatEmissionValue(dividesPerHectares(calculation.emissions.baseline as number), true));
      }
    }
  };

  const handleEmptyState = () => {
    const emptyPayoutIconName = "klim/funding";
    const emptyPayoutTitle = t("sidebar.emptyPayoutTitle");
    const emptyPayoutDescription = t("sidebar.emptyPayoutDescription");

    const emptyEmissionIconName = "tabler/cloud-upload";
    const emptyEmissionTitle = t("sidebar.emptyEmissionTitle");
    const emptyEmissionDescription = t("sidebar.emptyEmissionDescription");

    if (resultType === CROP_EXPLORER_RESULT_TYPE.PAYOUT) {
      setEmptyStateValue({
        iconName: emptyPayoutIconName,
        title: emptyPayoutTitle,
        description: emptyPayoutDescription,
      });
    }
    if (resultType === CROP_EXPLORER_RESULT_TYPE.EMISSION) {
      setEmptyStateValue({
        iconName: emptyEmissionIconName,
        title: emptyEmissionTitle,
        description: emptyEmissionDescription,
      });
    }
  };

  const dividesPerHectares = (value: number | null) => {
    // This component is not rendered in the admin section, so we are sure it's of type CropExplorationResponse
    if (!(cropExploration as CropExplorationResponse)?.hectares || value === null) return 0;
    const { hectares } = cropExploration as CropExplorationResponse;
    return value / hectares;
  };

  const options = [
    { label: t("sidebar.toggleOptionYear"), value: CROP_EXPLORER_RESULT_TOGGLE_OPTION.YEAR },
    { label: t("sidebar.toggleOptionCropPractice"), value: CROP_EXPLORER_RESULT_TOGGLE_OPTION.CROP_PRACTICE },
  ];

  useEffect(() => {
    setValues();
    handleEmptyState();
  }, [calculation, calculationType]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {showResults ? (
        <div data-testid={`crop-explorer-result-card-${resultType.toLowerCase()}`} className="crop-explorer-result-card">
          <div className="crop-explorer-result-card container">
            <div className={`crop-explorer-result-card container top ${hasNegativeImpact && "negative-impact"} ${!hasBaseline && "missing-baseline"}`}>
              {!hasBaseline && resultType === CROP_EXPLORER_RESULT_TYPE.EMISSION ? (
                <MissingBaselineEmissionCardHeader title={t("sidebar.unavailableBaselineTitle")} description={t("sidebar.unavailableBaselineDescription")} />
              ) : (
                <HeaderSectionCalculations
                  avg={avg}
                  min={min}
                  max={max}
                  resultType={resultType}
                  hasNegativeImpact={hasNegativeImpact}
                  t={t}
                  sequesteredAvg={sequesteredAvg}
                  hasPayout={!!calculation?.payout}
                  baseline={calculation?.emissions.baseline as number}
                />
              )}
              {resultType === CROP_EXPLORER_RESULT_TYPE.PAYOUT && hasNegativeImpact && (
                <LinkMoreInfo
                  dataTestId="crop-explorer-result-card-negative-payout-link-more-info"
                  className="crop-explorer-result-card info-link"
                  theme="grey-small"
                  onClick={() => setShowNegativePayoutDrawer(true)}
                >
                  {t("sidebar.negativePayoutCmsDrawer")}
                </LinkMoreInfo>
              )}
              <Divider />
              <KlimRadioGroup
                className="margin-top-100 margin-bottom-100"
                type="other"
                size="small"
                dataTestId="crop-explorer-result-card-toggle"
                options={options}
                onChange={(e) => setToggleOption(e.target.value)}
                value={toggleOption}
                optionType="button"
                buttonStyle="solid"
              />
              <Graph
                type={resultType}
                toggleOption={toggleOption}
                calculation={calculation}
                t={t}
                yearRange={yearRange ?? {}}
                calculationType={calculationType}
                dividesPerHectares={dividesPerHectares}
                hasBaseline={hasBaseline}
              />
            </div>
            {resultType === CROP_EXPLORER_RESULT_TYPE.PAYOUT && hasNegativeImpact && <MitigationMessage t={t} />}
          </div>
        </div>
      ) : (
        <EmptyDataView {...(emptyStateValue as CropExplorerResultCardPropsEmpty)} />
      )}
      <ExtraInfo setVisible={setShowNegativePayoutDrawer} type="negative-payout" visible={showNegativePayoutDrawer} />
    </>
  );
};

const HeaderSectionCalculations = ({
  avg,
  min,
  max,
  sequesteredAvg,
  hasNegativeImpact,
  hasPayout,
  resultType,
  t,
  baseline,
}: {
  avg: string | undefined;
  min: string | undefined;
  max: string | undefined;
  sequesteredAvg: string | undefined;
  hasNegativeImpact: boolean | undefined;
  hasPayout: boolean;
  resultType: CROP_EXPLORER_RESULT_TYPE;
  t: TFunction;
  baseline: number;
}) => (
  <>
    <Flex data-testid="crop-explorer-result-card-avg-container" vertical={false}>
      <h1 data-testid="crop-explorer-result-card-avg-value" className={`crop-explorer-result-card avg value ${hasNegativeImpact && "negative-impact"}`}>
        {!hasPayout ? sequesteredAvg : avg}
      </h1>
      {resultType === CROP_EXPLORER_RESULT_TYPE.EMISSION && (
        <>
          <h3 className={`crop-explorer-result-card avg unit ${hasNegativeImpact && "negative-impact"}`}>
            &nbsp;
            <>
              CO<sub>2</sub>
            </>
            &nbsp;
          </h3>
          <p className={`crop-explorer-result-card avg description ${hasNegativeImpact && "negative-impact"}`}>
            {!hasPayout && baseline >= 0
              ? t("sidebar.emittedOnAverage")
              : !hasPayout && baseline < 0
              ? t("sidebar.sequesteredOnAverage")
              : hasNegativeImpact
              ? t("sidebar.emissionNegativeImpact")
              : t("sidebar.emissionPositiveImpact")}
          </p>
        </>
      )}
    </Flex>
    <p data-testid="crop-explorer-result-card-min-max" className="crop-explorer-result-card  min-max margin-top-25">
      {hasPayout &&
        t("sidebar.minMax", {
          min: min,
          max: max,
        })}
    </p>
  </>
);

const MitigationMessage = ({ t }: { t: TFunction }) => (
  <Flex data-testid="crop-explorer-result-card-negative-payout-mitigation-message" className="crop-explorer-result-card negative-payout" vertical>
    <p>{t("sidebar.mitigationMessageTitle")}</p>
    <li>{t("sidebar.mitigationMessage1")}</li>
    <li>{t("sidebar.mitigationMessage2")}</li>
    <li>{t("sidebar.mitigationMessage3")}</li>
  </Flex>
);

export const Graph = ({
  toggleOption,
  calculation,
  type,
  yearRange,
  calculationType,
  hasBaseline,
  t,
  dividesPerHectares,
}: {
  toggleOption: CROP_EXPLORER_RESULT_TOGGLE_OPTION;
  calculation?: Calculation;
  type: CROP_EXPLORER_RESULT_TYPE;
  yearRange: YearRange;
  calculationType: CROP_EXPLORER_RESULT_CALCULATION_TYPE;
  hasBaseline: boolean;
  t: TFunction;
  dividesPerHectares: (value: number | null) => number;
}) => {
  if (!calculation) return null;

  if (type === CROP_EXPLORER_RESULT_TYPE.PAYOUT && calculation.payout) {
    if (toggleOption === CROP_EXPLORER_RESULT_TOGGLE_OPTION.YEAR)
      return (
        <div data-testid="crop-explorer-result-card-graph-payout-year">
          <CropExplorerBarChart
            labels={calculation.payout.yearlyPayouts.map((payouts) => payouts.year.toString())}
            datasets={[
              {
                label: "",
                data:
                  calculationType === CROP_EXPLORER_RESULT_CALCULATION_TYPE.TOTAL
                    ? calculation.payout.yearlyPayouts.map((payouts) => payouts.values.avg)
                    : calculation.payout.yearlyPayouts.map((payouts) => dividesPerHectares(payouts.values.avg)),
              },
            ]}
            formatFunction={formatPayoutValue}
            dividePerHa={calculationType === CROP_EXPLORER_RESULT_CALCULATION_TYPE.PER_HA}
          />
        </div>
      );

    if (toggleOption === CROP_EXPLORER_RESULT_TOGGLE_OPTION.CROP_PRACTICE)
      return (
        <div data-testid="crop-explorer-result-card-graph-payout-crop-practice">
          <CropExplorerBarChart
            labels={[t("sidebar.mains"), t("sidebar.sides"), t("sidebar.residues")]}
            datasets={[
              {
                label: "",
                data:
                  calculationType === CROP_EXPLORER_RESULT_CALCULATION_TYPE.TOTAL
                    ? [calculation.payout.crops.main, calculation.payout.crops.side, calculation.payout.crops.residue]
                    : [
                        dividesPerHectares(calculation.payout.crops.main),
                        dividesPerHectares(calculation.payout.crops.side),
                        dividesPerHectares(calculation.payout.crops.residue),
                      ],
              },
            ]}
            formatFunction={formatPayoutValue}
            dividePerHa={calculationType === CROP_EXPLORER_RESULT_CALCULATION_TYPE.PER_HA}
          />
        </div>
      );
  }
  if (type === CROP_EXPLORER_RESULT_TYPE.EMISSION) {
    if (toggleOption === CROP_EXPLORER_RESULT_TOGGLE_OPTION.YEAR)
      return (
        <div data-testid="crop-explorer-result-card-graph-emission-year">
          {hasBaseline && (
            <p data-testid="crop-explorer-result-card-crop-practice-baseline-info" className="crop-explorer-result-card baseline-info">
              {t("sidebar.baseLineInfo", {
                yearStart: yearRange.start ?? "",
                yearEnd: yearRange.end ?? "",
                baselineCo2Value:
                  calculationType === CROP_EXPLORER_RESULT_CALCULATION_TYPE.TOTAL
                    ? formatEmissionValue(calculation.emissions.baseline as number)
                    : formatEmissionValue(dividesPerHectares(calculation.emissions.baseline as number), true),
              })}
            </p>
          )}
          <CropExplorerBarChart
            labels={calculation.emissions.yearlyTotals.map((yearlyTotal) => yearlyTotal.year.toString())}
            datasets={[
              {
                label: "",
                data:
                  calculationType === CROP_EXPLORER_RESULT_CALCULATION_TYPE.TOTAL
                    ? calculation.emissions.yearlyTotals.map((yearlyTotal) => yearlyTotal.value)
                    : calculation.emissions.yearlyTotals.map((yearlyTotal) => dividesPerHectares(yearlyTotal.value)),
              },
            ]}
            formatFunction={formatEmissionValue}
            baseline={
              calculationType === CROP_EXPLORER_RESULT_CALCULATION_TYPE.TOTAL
                ? calculation.emissions.baseline
                : dividesPerHectares(calculation.emissions.baseline)
            }
            reversedColorScheme
            dividePerHa={calculationType === CROP_EXPLORER_RESULT_CALCULATION_TYPE.PER_HA}
          />
        </div>
      );

    if (toggleOption === CROP_EXPLORER_RESULT_TOGGLE_OPTION.CROP_PRACTICE)
      return (
        <div data-testid="crop-explorer-result-card-graph-emission-crop-practice">
          {typeof calculation.emissions.baseline === "number" && (
            <p data-testid="crop-explorer-result-card-crop-practice-baseline-info" className="crop-explorer-result-card baseline-info">
              {t("sidebar.baseLineInfo", {
                yearStart: yearRange.start ?? "",
                yearEnd: yearRange.end ?? "",
                baselineCo2Value:
                  calculationType === CROP_EXPLORER_RESULT_CALCULATION_TYPE.TOTAL
                    ? formatEmissionValue(calculation.emissions.baseline as number)
                    : formatEmissionValue(dividesPerHectares(calculation.emissions.baseline as number), true),
              })}
            </p>
          )}
          <CropExplorerBarChart
            labels={[t("sidebar.mains"), t("sidebar.sides"), t("sidebar.residues")]}
            datasets={[
              {
                label: t("sidebar.pastCrops"),
                data:
                  calculationType === CROP_EXPLORER_RESULT_CALCULATION_TYPE.TOTAL
                    ? [calculation.emissions.crops.main.past, calculation.emissions.crops.side.past, calculation.emissions.crops.residue.past]
                    : [
                        dividesPerHectares(calculation.emissions.crops.main.past),
                        dividesPerHectares(calculation.emissions.crops.side.past),
                        dividesPerHectares(calculation.emissions.crops.residue.past),
                      ],
              },
              {
                label: t("sidebar.futureCrops"),
                data:
                  calculationType === CROP_EXPLORER_RESULT_CALCULATION_TYPE.TOTAL
                    ? [calculation.emissions.crops.main.future, calculation.emissions.crops.side.future, calculation.emissions.crops.residue.future]
                    : [
                        dividesPerHectares(calculation.emissions.crops.main.future),
                        dividesPerHectares(calculation.emissions.crops.side.future),
                        dividesPerHectares(calculation.emissions.crops.residue.future),
                      ],
              },
            ]}
            formatFunction={formatEmissionValue}
            reversedColorScheme
            dividePerHa={calculationType === CROP_EXPLORER_RESULT_CALCULATION_TYPE.PER_HA}
          />
        </div>
      );
  }
};

const EmptyDataView = ({ iconName, title, description }: { iconName: string; title: string; description: string }) => (
  <div data-testid="crop-explorer-result-card-empty" className="crop-explorer-result-card">
    <div className="crop-explorer-result-card container empty">
      <Flex vertical={true} align="center" justify="center" style={{ height: "100%" }}>
        <Icon className="margin-bottom-100" iconName={iconName} size="xl" />
        <p data-testid="crop-explorer-result-card-empty-title" className="paragraph-s">
          {title}
        </p>
        <p data-testid="crop-explorer-result-card-empty-description" className="paragraph-xs">
          {description}
        </p>
      </Flex>
    </div>
  </div>
);

const MissingBaselineEmissionCardHeader = ({ title, description }: { title: string; description: string }) => (
  <Flex vertical gap={4} className="padding-bottom-100" data-testid="missing-baseline-emission-card-header">
    <p className="missing-baseline-emission-card-title">{title}</p>
    <p className="paragraph-xs grey-600-text margin-0">{description}</p>
  </Flex>
);

const formatPayoutValue = (value?: number, dividePerHectare?: boolean): string => {
  if (!value) return formatCurrency(0);
  const formattedValue = formatCurrency(value);
  let prefix = "+";

  if (value < 0) {
    prefix = "";
  }
  return dividePerHectare ? `${prefix}${formattedValue}/ha` : `${prefix}${formattedValue}`;
};

const formatEmissionValue = (value?: number, dividePerHectare?: boolean): string => {
  if (!value) return formatCO2Amount(0).split("t").join(" t");
  const formattedValue = formatCO2Amount(value).split("t").join(" t");

  let prefix = "+";

  if (value < 0) {
    prefix = "";
  }
  return dividePerHectare ? `${prefix}${formattedValue}/ha` : `${prefix}${formattedValue}`;
};
